import React, {
  forwardRef,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
} from 'react';
import { ButtonProps } from '../../Button/Button';

import { Menu, MenuButton } from '@headlessui/react';
// todo: remove this, create a new type
import { SortDirection } from '@payaca/types/listViewTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import Checkbox, { CheckboxProps } from '../../Checkbox/Checkbox';
import ContextMenu from '../../Dropdown/ContextMenu';
import DropdownMenuItems from '../../Dropdown/DropdownMenuItems';
import EmptyState from '../../EmptyState/EmptyState';
import FloatingTooltip from '../../Tooltip/FloatingTooltip';
import TooltipCore from '../../Tooltip/TooltipCore';
import UntitledLoading from '../../UntitledLoading/UntitledLoading';

export type TColumnRender<T extends Record<string, any>, F extends keyof T> = (
  field: T[F],
  row: T,
  index: number
) => ReactNode;

export interface IColumnProps<
  T extends Record<string, any>,
  F extends keyof T = keyof T,
  S extends string = string,
> {
  header: string;
  field: F;
  render?: TColumnRender<T, F>;
  className?: string;
  onSort?: (field: F | S, sortDirection: SortDirection) => void;
  sortByName?: S; // alternative sortBy name instead of field
  hiddenHeader?: boolean;
  align?: 'start' | 'center' | 'end';
}

export type TTableBulkAction<T extends Record<string, any>> = Omit<
  ButtonProps,
  'onClick' | 'size'
> & {
  label: string;
  onClick: (rows: T[]) => void;
  disabledTooltip?: string;
};

export type TTableFilter<T extends Record<string, any>> = {
  id: string;
  label: string;
  filterFn: (rows: T[]) => T[];
};

export type TTableRowAction<T extends Record<string, any>> = {
  label: string;
  onClick: (row: T) => void;
  isDisabled?: (row: T) => boolean;
  disabledReasons?: string[];
  isProcessing?: boolean;
};

export type TTableShouldDisableItem<T extends Record<string, any>> = (
  row: T
) => {
  disabled: boolean;
  tooltip?: string;
};

export interface IAdvancedTableProps<T extends Record<string, any>> {
  items: T[];
  uniqueKey: keyof T;
  isLoading?: boolean;
  selectedItems?: T[];
  setSelectedItems?: (items: T[]) => void;
  enableSelectAll?: boolean;
  // todo: maybe a function that doesn't fire for every row and every render?
  shouldDisableItem?: TTableShouldDisableItem<T>;
  itemActions?: TTableRowAction<T>[] | ((row: T) => TTableRowAction<T>[]);
  onClickRow?: (row: T) => void;
  sortBy?: keyof T;
  sortDirection?: SortDirection;
  hideEmptyState?: boolean;
  emptyText?: string;
  /**
   * @deprecated Use footerColumns instead. This pattern is being phased out.
   */
  doNotUseFooterRow?: ReactNode;
  footerColumns?: Array<{
    span: number;
    render: () => ReactNode;
    className?: string;
  }>;
}

export const Column = <T extends Record<string, any>, F extends keyof T>(
  props: IColumnProps<T, F>
) => <></>;

const twAlignMap = {
  start: { text: 'text-start', flex: 'justify-start' },
  center: { text: 'text-center', flex: 'justify-center' },
  end: { text: 'text-end', flex: 'justify-end' },
};

const Table_ = <T extends Record<string, any>>(
  props: PropsWithChildren<IAdvancedTableProps<T>>
) => {
  const {
    items,
    uniqueKey,
    selectedItems = [],
    setSelectedItems,
    isLoading = false,
    shouldDisableItem,
    enableSelectAll = false,
    itemActions,
    children,
    onClickRow,
    sortBy,
    sortDirection,
    hideEmptyState = false,
    emptyText,
    doNotUseFooterRow,
    footerColumns,
  } = props;

  const columns = useMemo(() => {
    return React.Children.toArray(children).reduce<IColumnProps<T>[]>(
      (acc, child) => {
        if (!React.isValidElement(child)) {
          return acc;
        }

        // In React 19, props are now 'unknown' instead of 'any'
        const props = child.props as any;
        if (
          typeof props.header === 'undefined' ||
          typeof props.field === 'undefined'
        ) {
          return acc;
        }

        acc.push(props);

        return acc;
      },
      []
    );
  }, [children]);

  useEffect(() => {
    // Remove any selected rows that are no longer in the paginated data (data that is visible on the screen)
    setSelectedItems?.(
      selectedItems.filter(
        (i) => !!items.find((j) => j[uniqueKey] === i[uniqueKey])
      )
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'setSelectedItems', and 'selectedItems'
  }, [items, uniqueKey]);

  const enabledItems = useMemo(() => {
    if (!shouldDisableItem) return items;

    return items.filter((row) => !shouldDisableItem(row).disabled);
  }, [items, shouldDisableItem]);

  const renderColumnHeader = (column: IColumnProps<T>, i: number) => {
    const {
      header,
      field,
      className,
      onSort,
      sortByName,
      hiddenHeader,
      align: textAlign,
    } = column;

    // oxlint-disable-next-line typescript-eslint(no-unnecessary-type-assertion) -- needed: field is keyof T but TS widens to string | number | symbol without the assertion
    const sortingName = sortByName || (field as keyof T);

    const Header = () => {
      return <span className={clstx(hiddenHeader && 'sr-only')}>{header}</span>;
    };

    const twAlign = twAlignMap[textAlign || 'start'];

    return (
      <th
        key={`${String(field)}-${i}`}
        scope="col"
        className={clstx(twAlign.text, className, onSort ? '' : 'px-6 py-3')}
      >
        {onSort ? (
          <Menu>
            <MenuButton
              className={clstx(
                'tracking-wider flex cursor-pointer w-full items-center gap-x-1 bg-transparent px-6 py-3 text-start font-medium uppercase focus:bg-gray-100 focus:outline-none',
                twAlign.flex
              )}
            >
              <Header />

              <div className="ml-1 flex flex-col">
                <UntitledIcon
                  name="chevron-up"
                  className={clstx(
                    `-mb-[4px] h-3 w-3`,
                    sortBy !== sortingName && 'text-gray-500',
                    sortBy === sortingName &&
                      sortDirection !== SortDirection.ASCENDING &&
                      'invisible'
                  )}
                />
                <UntitledIcon
                  name="chevron-down"
                  className={clstx(
                    `h-3 w-3`,
                    sortBy !== sortingName && 'text-gray-500',
                    sortBy === sortingName &&
                      sortDirection !== SortDirection.DESCENDING &&
                      'invisible'
                  )}
                />
              </div>
            </MenuButton>

            <DropdownMenuItems
              anchor="start"
              items={[
                {
                  label: (
                    <>
                      <UntitledIcon name="arrow-up" className="h-4 w-4" />
                      Sort ascending
                    </>
                  ),
                  onClick: () => onSort?.(sortingName, SortDirection.ASCENDING),
                },
                {
                  label: (
                    <>
                      <UntitledIcon name="arrow-down" className="h-4 w-4" />
                      Sort descending
                    </>
                  ),
                  onClick: () =>
                    onSort?.(sortingName, SortDirection.DESCENDING),
                },
              ]}
            />
          </Menu>
        ) : (
          <Header />
        )}
      </th>
    );
  };

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <UntitledLoading />
        </div>
      ) : !hideEmptyState && items.length === 0 ? (
        <EmptyState className="py-6" text={emptyText} />
      ) : (
        <>
          <div className="typography:prose overflow-x-auto">
            <table className="min-w-full border-collapse divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {!!setSelectedItems && (
                    <>
                      {enableSelectAll ? (
                        <CheckboxElement
                          header
                          label="Select all rows"
                          disabled={enabledItems.length === 0}
                          checked={
                            enabledItems.length
                              ? selectedItems.length === enabledItems.length
                              : false
                          }
                          onChange={(e) =>
                            e.target.checked
                              ? setSelectedItems(enabledItems)
                              : setSelectedItems([])
                          }
                        />
                      ) : (
                        <th></th>
                      )}
                    </>
                  )}

                  {columns.map(renderColumnHeader)}

                  {itemActions && (
                    <th
                      scope="col"
                      className="sticky right-0 bg-gray-50 dark:bg-gray-800"
                    >
                      <span className="sr-only">Row actions</span>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((row, itemIndex) => {
                  const isRowDisabled = shouldDisableItem?.(row);

                  return (
                    <tr
                      key={row[uniqueKey]}
                      onClick={onClickRow ? () => onClickRow(row) : undefined}
                      className={
                        onClickRow
                          ? 'group/row hover:cursor-pointer hover:bg-gray-100'
                          : ''
                      }
                    >
                      {!!setSelectedItems && (
                        <FloatingTooltip
                          enabled={
                            (isRowDisabled?.disabled &&
                              !!isRowDisabled?.tooltip?.length) ||
                            false
                          }
                          showArrow
                        >
                          <CheckboxElement
                            label={`Select row ${row[uniqueKey]}`}
                            checked={
                              !!selectedItems.find(
                                (i) => i[uniqueKey] === row[uniqueKey]
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            // ToDo: update type?
                            onChange={(e: any) =>
                              e.target.checked
                                ? setSelectedItems([...selectedItems, row])
                                : setSelectedItems(
                                    selectedItems.filter(
                                      (i) => i[uniqueKey] !== row[uniqueKey]
                                    )
                                  )
                            }
                            elementClassName={
                              onClickRow ? 'group-hover/row:bg-gray-100' : ''
                            }
                            disabled={isRowDisabled?.disabled}
                          />

                          <TooltipCore>
                            <div className="max-w-[150px]">
                              {isRowDisabled?.tooltip}
                            </div>
                          </TooltipCore>
                        </FloatingTooltip>
                      )}

                      {columns.map(({ field, render, className, align }, i) => (
                        <td
                          key={`${String(field)}-${i}`}
                          className={clstx(
                            twAlignMap[align || 'start'].text,
                            'whitespace-nowrap px-6 py-4',
                            className
                          )}
                        >
                          {render
                            ? render(row[field], row, itemIndex)
                            : row[field]}
                        </td>
                      ))}

                      {itemActions && (
                        <td
                          className={`sticky right-0 w-[80px] px-6 py-4 bg-white${
                            onClickRow ? ' group-hover/row:bg-gray-100' : ''
                          }`}
                          onClick={(e) => onClickRow && e.stopPropagation()}
                        >
                          <ContextMenu
                            size="xs"
                            variant="white"
                            className={clstx(
                              'aspect-square',
                              onClickRow ? 'hover:!bg-gray-200' : ''
                            )}
                            label={`Actions for row ${row[uniqueKey]}`}
                            menuItems={(typeof itemActions === 'function'
                              ? itemActions(row)
                              : itemActions
                            ).map(({ onClick, isDisabled, ...rest }) => {
                              const disabled = !!isDisabled?.(row);
                              return {
                                ...rest,
                                disabled,
                                onClick: () => onClick(row),
                              };
                            })}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>

              {doNotUseFooterRow && (
                <tfoot>
                  <tr>
                    <td className="normal-case" colSpan={columns.length + 2}>
                      {doNotUseFooterRow}
                    </td>
                  </tr>
                </tfoot>
              )}
              {footerColumns && (
                <tfoot className="bg-gray-50 dark:bg-gray-800">
                  {footerColumns.map((col, i) => (
                    <td
                      key={i}
                      colSpan={col.span}
                      className={clstx('px-6 py-3', col.className)}
                    >
                      {col.render()}
                    </td>
                  ))}
                  {/* In case provided footer cols don't span full table width, this will fill the gap */}
                  <td colSpan={999}></td>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </>
  );
};

const CheckboxElement = forwardRef<
  HTMLInputElement,
  CheckboxProps & { header?: boolean; elementClassName?: string }
>(({ header = false, className, elementClassName, ...rest }, ref) => {
  const El = header ? 'th' : 'td';

  return (
    <El
      className={clstx(
        'sticky left-0 w-12 min-w-12 py-3 pe-4 ps-4',
        header ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white',
        !header && elementClassName
      )}
    >
      <div className="flex h-5 items-center">
        <Checkbox
          ref={ref}
          className={'cursor-pointer' + (className ? ' ' + className : '')}
          hiddenLabel
          {...rest}
        />
      </div>
    </El>
  );
});

export const Table = Object.assign(Table_, {
  Column,
});
